/**
 * Modal to request the main URL of the tenant
 */
import { useState, useEffect } from 'react'
import { Button } from '@trafiflow/shared-ui'

interface WebsiteUrlModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: (url: string) => Promise<void>
  currentUrl?: string
}

export default function WebsiteUrlModal({
  isOpen,
  onClose,
  onSave,
  currentUrl = '',
}: WebsiteUrlModalProps) {
  const [url, setUrl] = useState(currentUrl)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Update URL when currentUrl changes (e.g., when modal opens)
  useEffect(() => {
    if (isOpen) {
      setUrl(currentUrl)
      setError(null)
    }
  }, [isOpen, currentUrl])

  if (!isOpen) return null

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    // Validate URL
    if (!url.trim()) {
      setError('Please enter a valid URL')
      return
    }

    // Normalize URL: add https:// if no protocol
    let normalizedUrl = url.trim()
    if (!normalizedUrl.startsWith('http://') && !normalizedUrl.startsWith('https://')) {
      normalizedUrl = `https://${normalizedUrl}`
    }

    // Validate basic URL format
    try {
      new URL(normalizedUrl)
    } catch {
      setError('Please enter a valid URL (e.g., example.com or https://example.com)')
      return
    }

    setLoading(true)
    try {
      await onSave(normalizedUrl)
      // Update the URL state to the normalized value
      setUrl(normalizedUrl)
      onClose()
    } catch (err: unknown) {
      const error = err as { message?: string }
      setError(error.message || 'Error saving URL. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    if (!loading) {
      setUrl(currentUrl)
      setError(null)
      onClose()
    }
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          className="relative bg-tf-bg-card rounded-lg shadow-xl max-w-md w-full p-6"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-tf-text">
              Company Main URL
            </h2>
            <button
              onClick={handleClose}
              disabled={loading}
              className="text-tf-text-muted hover:text-tf-text-muted transition-colors disabled:opacity-50"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <p className="text-sm text-tf-text-muted mb-4">
            {currentUrl
              ? 'Update your company\'s main URL. Saving will verify and set up your company profile if needed.'
              : 'Please enter your company\'s main URL (institutional website). This information will be used to set up your company profile and enable AI-powered insights.'}
          </p>

          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label
                htmlFor="website-url"
                className="block text-sm font-medium text-tf-text-sub mb-2"
              >
                Website URL
              </label>
              <input
                id="website-url"
                type="text"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://example.com"
                disabled={loading}
                className="w-full px-3 py-2 border border-tf-border rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary disabled:bg-tf-bg-hover disabled:cursor-not-allowed"
              />
              {error && (
                <p className="mt-2 text-sm text-red-600">{error}</p>
              )}
              <p className="mt-2 text-xs text-tf-text-muted">
                You can enter just the domain (e.g., example.com) or the full URL (e.g., https://example.com)
              </p>
            </div>

            <div className="flex justify-end space-x-3">
              <Button
                type="button"
                variant="secondary"
                onClick={handleClose}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={loading || !url.trim()}
              >
                {loading ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

